import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { AuthService } from '../../../shared/services/auth.service';
import { PaymentsService } from '../../../shared/services/payments.service';
import { WalletsService } from '../../../shared/services/wallets.service';
import { WithdrawalsService } from '../../../shared/services/withdrawals.service';
import { PaymentMethod } from '../../../shared/types/payment.types';
import {
  WITHDRAWAL_STATUS_BADGE,
  WITHDRAWAL_STATUS_LABEL,
  WithdrawalRequestView,
} from '../../../shared/types/withdrawal.types';
import {
  ACCOUNT_STATUS_LABEL,
  ACCOUNT_TYPE_LABEL,
  WalletSnapshotResponse,
} from '../../../shared/types/wallet.types';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { LocaleService } from '../../../shared/services/locale.service';

/**
 * Page "Mon wallet" — snapshot direct du wallet OWNER + actions financières.
 *
 * Inclut le flow de **recharge CREDIT** (WALLET_TOPUP) :
 *   1. L'owner saisit montant + numéro mobile money + méthode (Wave/OM/Free).
 *   2. On appelle `POST /payment/api/transactions/initiate` avec
 *      `metadata: { type: WALLET_TOPUP, ownerType: OWNER, ownerRef }`.
 *   3. La réponse contient un `paymentUrl` GUTouch → on ouvre dans un nouvel
 *      onglet (ou expose le `qrCode` / `deepLink` mobile).
 *   4. Au callback GUTouch, wallet-service consomme `nawam.payment.result`
 *      et crédite le CREDIT (cf. `PaymentResultListener.handleTopUp`).
 *
 * Aucune action à faire côté front après l'init : le user revient et le
 * snapshot reflète automatiquement le nouveau solde.
 */
@Component({
  selector: 'app-wallets-list',
  imports: [CommonModule, RouterModule, ReactiveFormsModule, DecimalPipe, PageHeaderComponent, DataStateComponent, ModalComponent, PrettySelectComponent, TPipe, LocalizedDatePipe],
  templateUrl: './wallets-list.component.html',
})
export class WalletsListComponent implements OnInit {
  private readonly locale = inject(LocaleService);
  private readonly walletsService = inject(WalletsService);
  private readonly withdrawalsService = inject(WithdrawalsService);
  private readonly paymentsService = inject(PaymentsService);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly snapshot = signal<WalletSnapshotResponse | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  /** Bandeau transitoire (résultat d'un retrait). */
  readonly flash = signal<string | null>(null);

  readonly accountTypeLabel = ACCOUNT_TYPE_LABEL;
  readonly accountStatusLabel = ACCOUNT_STATUS_LABEL;

  /** true si le CREDIT est négatif (dette en cours). */
  readonly inDebt = computed(() => {
    const snap = this.snapshot();
    return !!snap && Number(snap.credit?.balance ?? 0) < 0;
  });

  // ── Top-up modal (WALLET_TOPUP) ────────────────────────────────────────
  readonly topupOpen = signal(false);
  readonly topupInitiating = signal(false);
  readonly topupError = signal<string | null>(null);
  /**
   * #58 — montant pré-rempli quand on ouvre le top-up depuis une **demande de
   * paiement de dette** (deep-link de la notif `DEBT_PAYMENT_REQUEST`, matrix
   * param `payDebt`). Non-null ⇒ le modal affiche le contexte « paiement de
   * dette ». La recharge CREDIT solde automatiquement la dette au retour.
   */
  readonly debtPrefill = signal<number | null>(null);
  /** Méthodes de paiement chargées à la première ouverture du modal. */
  readonly methods = signal<PaymentMethod[]>([]);
  readonly methodsLoading = signal(false);

  readonly methodOptions = computed<PrettySelectOption<string>[]>(() =>
    this.methods()
      .filter((m) => !!m.serviceCode)
      .map((m) => ({ value: m.serviceCode as string, label: m.name ?? m.serviceCode! })),
  );

  readonly topupForm = this.fb.group({
    amount: [5000, [Validators.required, Validators.min(100)]],
    serviceCode: ['', [Validators.required]],
    recipientNumber: ['', [Validators.required, Validators.minLength(9)]],
  });

  // ── Retrait BALANCE (#57) ──────────────────────────────────────────────
  readonly withdrawOpen = signal(false);
  readonly withdrawSubmitting = signal(false);
  readonly withdrawError = signal<string | null>(null);
  readonly withdrawForm = this.fb.group({
    amount: [0, [Validators.required, Validators.min(100)]],
    serviceCode: ['', [Validators.required]],
    recipientNumber: ['', [Validators.required, Validators.minLength(9)]],
    reason: [''],
  });

  // ── Mes retraits ───────────────────────────────────────────────────────
  readonly withdrawals = signal<WithdrawalRequestView[]>([]);
  readonly wLoading = signal(false);
  readonly wPage = signal(1);
  readonly wTotalPages = signal(0);
  readonly wTotalItems = signal(0);
  readonly withdrawalStatusLabel = WITHDRAWAL_STATUS_LABEL;
  readonly withdrawalStatusBadge = WITHDRAWAL_STATUS_BADGE;

  /** Lien vers la fiche détail du wallet (page existante avec ledger). */
  detailLink(ownerRef: number): unknown[] {
    return ['/my-wallet', 'OWNER', ownerRef];
  }

  ngOnInit(): void {
    this.fetch();
    this.loadWithdrawals();
    // #58 — arrivée depuis une demande de paiement de dette : ouvrir le top-up
    // pré-rempli du montant (matrix param `payDebt` posé par le deep-link notif).
    const payDebt = this.route.snapshot.paramMap.get('payDebt');
    const amount = payDebt != null ? Number(payDebt) : NaN;
    if (Number.isFinite(amount) && amount > 0) {
      this.openTopup(amount);
    }
  }

  fetch(): void {
    this.loading.set(true);
    this.error.set(null);
    this.walletsService
      .getMine('OWNER')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (snap) => {
          this.snapshot.set(snap);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.snapshot.set(null);
          this.loading.set(false);
          this.error.set(
            toErrorMessage(err, 'errors.yourWalletLoadFailed', {
              404: 'errors.walletNotProvisioned',
            }),
          );
        },
      });
  }

  // ── Top-up ─────────────────────────────────────────────────────────────
  openTopup(prefillAmount?: number): void {
    const user = this.auth.user();
    const isDebt = prefillAmount != null && prefillAmount > 0;
    this.debtPrefill.set(isDebt ? prefillAmount : null);
    this.topupForm.reset({
      amount: isDebt ? prefillAmount : 5000,
      serviceCode: '',
      recipientNumber: user?.phone ?? '',
    });
    this.topupError.set(null);
    this.topupOpen.set(true);
    // Lazy load des méthodes — seulement à la première ouverture.
    if (!this.methods().length) this.loadMethods();
  }

  closeTopup(): void {
    if (this.topupInitiating()) return;
    this.topupOpen.set(false);
  }

  private loadMethods(): void {
    this.methodsLoading.set(true);
    this.paymentsService
      .listActiveMethods()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => {
          this.methods.set(list ?? []);
          this.methodsLoading.set(false);
          // Pré-sélection de la première méthode si une seule existe.
          if (list?.length === 1 && list[0].serviceCode) {
            this.topupForm.patchValue({ serviceCode: list[0].serviceCode });
          }
        },
        error: () => {
          this.methods.set([]);
          this.methodsLoading.set(false);
        },
      });
  }

  submitTopup(): void {
    if (this.topupForm.invalid || this.topupInitiating()) {
      this.topupForm.markAllAsTouched();
      return;
    }
    const snap = this.snapshot();
    const user = this.auth.user();
    if (!snap || !user) return;

    const raw = this.topupForm.getRawValue();
    // `id` vient de /me (number) ; `userId` vient du JWT (string).
    const meId = user.id ?? (user.userId ? Number(user.userId) : undefined);
    const ownerRef = snap.credit.ownerRef;

    this.topupInitiating.set(true);
    this.topupError.set(null);

    this.paymentsService
      .initiate({
        amount: raw.amount,
        serviceCode: raw.serviceCode,
        recipientNumber: raw.recipientNumber.trim(),
        sourceService: 'wallet',
        sourceEntityId: String(ownerRef),
        sourceEntityType: 'OWNER',
        currency: 'XOF',
        userId: meId ?? undefined,
        metadata: JSON.stringify({
          type: 'WALLET_TOPUP',
          ownerType: 'OWNER',
          ownerRef,
        }),
        // Nouveau dispatcher : canal web owner + returnPath relatif. Le back
        // construit lui-même l'URL absolue via PAYMENT_RETURN_URL_WEB_OWNER
        // et redirige le browser vers /payment/result/wallet/topup?txId=…
        clientChannel: 'WEB_OWNER',
        returnPath: '/wallet/topup',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.topupInitiating.set(false);
          this.topupOpen.set(false);
          // Same-window redirect : le user paye chez GUTouch, qui rappelle
          // le dispatcher payment-service, qui redirige sur
          // /payment/result/wallet/topup avec txId+status. La SPA reprendra
          // depuis là (cf. PaymentResultComponent).
          const target = res.paymentUrl ?? res.deepLink;
          if (target) {
            window.location.href = target;
          }
        },
        error: (err: unknown) => {
          this.topupInitiating.set(false);
          this.topupError.set(
            toErrorMessage(err, 'errors.paymentInitFailed'),
          );
        },
      });
  }

  // ── Retrait BALANCE ────────────────────────────────────────────────────
  openWithdraw(): void {
    const snap = this.snapshot();
    const user = this.auth.user();
    const max = Number(snap?.withdrawableBalance ?? 0);
    this.withdrawForm.reset({
      amount: max > 0 ? max : 0,
      serviceCode: '',
      recipientNumber: user?.phone ?? '',
      reason: '',
    });
    // Borne dynamique : on ne retire pas plus que le solde retirable.
    this.withdrawForm.controls.amount.setValidators([
      Validators.required,
      Validators.min(100),
      Validators.max(max),
    ]);
    this.withdrawForm.controls.amount.updateValueAndValidity();
    this.withdrawError.set(null);
    this.withdrawOpen.set(true);
    if (!this.methods().length) this.loadMethods();
  }

  closeWithdraw(): void {
    if (this.withdrawSubmitting()) return;
    this.withdrawOpen.set(false);
  }

  submitWithdraw(): void {
    if (this.withdrawForm.invalid || this.withdrawSubmitting()) {
      this.withdrawForm.markAllAsTouched();
      return;
    }
    const raw = this.withdrawForm.getRawValue();
    this.withdrawSubmitting.set(true);
    this.withdrawError.set(null);
    this.withdrawalsService
      .createSelf('OWNER', {
        amount: raw.amount,
        recipientNumber: raw.recipientNumber.trim(),
        paymentMethodCode: raw.serviceCode,
        reason: raw.reason.trim() || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (w) => {
          this.withdrawSubmitting.set(false);
          this.withdrawOpen.set(false);
          this.flash.set(this.withdrawOutcomeMsg(w));
          this.fetch(); // le solde retirable a baissé (gel immédiat)
          this.loadWithdrawals();
        },
        error: (err: unknown) => {
          this.withdrawSubmitting.set(false);
          this.withdrawError.set(
            toErrorMessage(err, 'errors.withdrawFailed', {
              409: 'errors.amountAboveWithdrawable',
            }),
          );
        },
      });
  }

  private withdrawOutcomeMsg(w: WithdrawalRequestView): string {
    const amt = new Intl.NumberFormat('fr-FR').format(Number(w.amount));
    if (w.status === 'APPROVED' || w.status === 'COMPLETED') {
      return this.locale.t('wallets.withdrawApproved', { amount: amt });
    }
    return `Demande de retrait de ${amt} XOF enregistrée — en attente de validation. Le montant est déjà gelé sur votre solde retirable.`;
  }

  loadWithdrawals(): void {
    this.wLoading.set(true);
    this.withdrawalsService
      .listMine(this.wPage(), 10)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.withdrawals.set(res.data ?? []);
          this.wTotalItems.set(res.totalItems ?? 0);
          this.wTotalPages.set(res.totalPages ?? 0);
          this.wLoading.set(false);
        },
        error: () => {
          this.withdrawals.set([]);
          this.wLoading.set(false);
        },
      });
  }

  wGoToPage(p: number): void {
    if (p < 1 || p > this.wTotalPages() || p === this.wPage()) return;
    this.wPage.set(p);
    this.loadWithdrawals();
  }
}
